import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useApp } from "@/AppContext";
import { trackPartnerEvent } from "@/lib/partnerTrack";
import { Briefcase, MapPin, Tag, ArrowSquareOut, Handshake, Warning, CalendarBlank, ListChecks, Wrench, CurrencyDollar, Question, CaretRight, CheckCircle } from "@phosphor-icons/react";
import { ShareButtons } from "../components/ShareButtons";
import RelatedJobs from "@/components/RelatedJobs";

const SITE = "https://chambanow.com";

// Fallback: coerce a partner 'type' string to a valid schema.org employmentType
// (backend normally supplies job.schema.employment_type).
function _empType(raw) {
  const t = (raw || "").trim().toLowerCase();
  if (!t) return "FULL_TIME";
  if (t.includes("full")) return "FULL_TIME";
  if (t.includes("part")) return "PART_TIME";
  if (t.includes("contract")) return "CONTRACTOR";
  if (t.includes("temp") || t.includes("seasonal")) return "TEMPORARY";
  if (t.includes("intern")) return "INTERN";
  if (t.includes("per diem") || t.includes("per-diem")) return "PER_DIEM";
  if (t.includes("volunteer")) return "VOLUNTEER";
  return "FULL_TIME";
}

// Humanize a slug into a readable title (used as an H1 fallback while the real
// listing loads, so crawlers always find exactly one H1 on the page).
// Same-origin path for on-page <img> (works in preview AND prod); JSON-LD keeps
// the absolute chambanow.com URL for crawlers.
function _localLogo(u) {
  return (u || "").replace(/^https?:\/\/[^/]+/, "");
}

function humanize(slug) {
  return (slug || "")
    .split("-")
    .filter(Boolean)
    .map((w) => (w.length <= 2 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");
}

function upsertCanonical(href) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
  link.setAttribute("href", href);
}

// A live listing must be indexable; an expired/unknown one should NOT be (avoid
// soft-404s counting against the site), so we flip the robots meta accordingly.
function setRobots(content) {
  let el = document.querySelector('meta[name="robots"]');
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", "robots"); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

function setMeta(sel, attr, key, value) {
  let el = document.querySelector(sel);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", value);
}

export default function ExternalJobDetail() {
  const { slug } = useParams();
  const { t, user } = useApp();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const viewTrackedRef = useRef("");

  const selfUrl = `${SITE}/partner-jobs/${slug}`;
  const fallbackTitle = humanize(slug);

  // Set the SELF-referencing canonical immediately on mount — independent of the
  // API call — so crawlers never see the homepage canonical (from index.html) or
  // an external partner URL, even if the data is slow or the listing expired.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.title = `${fallbackTitle} | ChambaNow`;
    upsertCanonical(selfUrl);
    setMeta('meta[property="og:url"]', "property", "og:url", selfUrl);
    setMeta('meta[property="og:type"]', "property", "og:type", "article");
    // Inject the branded OG card IMMEDIATELY (before the fetch) so crawler
    // prerenders always capture the per-job image, never the generic default.
    const cardUrl = `${SITE}/api/og/partner-job/${slug}.png`;
    setMeta('meta[property="og:image"]', "property", "og:image", cardUrl);
    setMeta('meta[property="og:image:width"]', "property", "og:image:width", "1200");
    setMeta('meta[property="og:image:height"]', "property", "og:image:height", "630");
    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", cardUrl);
  }, [slug, selfUrl, fallbackTitle]);

  // Partner (aggregator) listings are indexable ONLY when fresh (within the 30-day
  // retention window) AND complete for Google-for-Jobs — the backend decides this and
  // returns `job.indexable`. Until the listing loads (or if it's stale/expired/unknown)
  // we keep noindex so a sitemap URL never renders a thin/expired page. Fresh, quality
  // listings flip to index,follow so they (re)appear in Google + Google Jobs.
  useEffect(() => {
    if (typeof document === "undefined") return;
    setRobots("noindex, follow");
  }, []);

  useEffect(() => {
    if (typeof document === "undefined" || !job) return;
    setRobots(job.indexable ? "index, follow" : "noindex, follow");
  }, [job]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setNotFound(false);
    axios.get(`${API}/jobs/external/${slug}`)
      .then((r) => { if (!cancel) setJob(r.data); })
      .catch(() => { if (!cancel) setNotFound(true); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [slug]);

  // Record an anonymous partner "job_detail_view" once per slug (StrictMode-safe).
  useEffect(() => {
    if (!job || !job.provider || viewTrackedRef.current === slug) return;
    viewTrackedRef.current = slug;
    trackPartnerEvent({
      provider: job.provider,
      event: "job_detail_view",
      job_id: job.external_id,
      job_category: job.type,
      job_location: job.location,
    });
  }, [job, slug]);

  // Enrich title + meta once the listing loads. Canonical stays SELF.
  useEffect(() => {
    if (!job || typeof document === "undefined") return;
    document.title = `${job.title}${job.company ? ` — ${job.company}` : ""} | ChambaNow`;
    const descTxt = (job.enrichment?.meta_description || job.enrichment?.overview || job.description || job.snippet || job.title || "").slice(0, 160).replace(/\s+/g, " ");
    const cardUrl = `${SITE}/api/og/partner-job/${slug}.png`;
    setMeta('meta[name="description"]', "name", "description", descTxt);
    setMeta('meta[property="og:title"]', "property", "og:title", `${job.title} | ChambaNow`);
    setMeta('meta[property="og:description"]', "property", "og:description", descTxt);
    setMeta('meta[property="og:image"]', "property", "og:image", cardUrl);
    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", `${job.title} | ChambaNow`);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", descTxt);
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", cardUrl);
    upsertCanonical(selfUrl);
    setMeta('meta[property="og:url"]', "property", "og:url", selfUrl);
  }, [job, selfUrl, slug]);

  // Share via the /api/share/* prerender URL so social bots (WhatsApp/FB/X) get a
  // rich branded card, while humans are instantly redirected to this page.

  const enr = job?.enrichment || null;

  const jobDesc = job
    ? [
        enr?.overview || job.description || job.snippet || job.title,
        enr?.responsibilities?.length ? "Responsibilities: " + enr.responsibilities.join("; ") + "." : "",
        enr?.skills?.length ? "Helpful skills: " + enr.skills.join("; ") + "." : "",
      ].filter(Boolean).join(" ")
    : "";

  const sc = job?.schema || {};
  const partnerAddr = { "@type": "PostalAddress", "addressCountry": "US" };
  if (sc.address_locality || job?.location) {
    partnerAddr.addressLocality = sc.address_locality || (job?.location || "").split(",")[0].trim();
  }
  if (sc.address_region) partnerAddr.addressRegion = sc.address_region;
  if (sc.postal_code) partnerAddr.postalCode = sc.postal_code;

  const jsonLd = job ? {
    "@context": "https://schema.org/",
    "@type": "JobPosting",
    "title": job.title,
    "description": jobDesc,
    "identifier": { "@type": "PropertyValue", "name": job.source || "Partner", "value": job.external_id },
    "datePosted": sc.date_posted || job.updated || job.first_seen,
    "validThrough": sc.valid_through,
    "employmentType": sc.employment_type || _empType(job.type),
    "hiringOrganization": {
      "@type": "Organization",
      "name": sc.org_name || job.company || job.source || "Partner employer",
      ...(sc.org_logo || job.company_logo ? { "logo": sc.org_logo || job.company_logo } : {}),
    },
    "jobLocation": { "@type": "Place", "address": partnerAddr },
    ...(sc.base_salary ? { "baseSalary": sc.base_salary } : {}),
    "directApply": true,
    "url": selfUrl,
  } : null;

  const faqLd = enr?.faqs?.length ? {
    "@context": "https://schema.org/",
    "@type": "FAQPage",
    "mainEntity": enr.faqs.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  } : null;

  const _pCity = (job?.location || "").split(",")[0].trim();
  const _pState = (job?.location || "").includes(",") ? (job.location.split(",")[1] || "").trim().slice(0, 2) : "";
  const _pCitySlug = _pCity
    ? `${_pCity.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}${_pState ? "-" + _pState.toLowerCase().replace(/[^a-z]/g, "") : ""}`
    : null;
  const breadcrumbLd = job ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Jobs", "item": `${SITE}/jobs` },
      ...(_pCity ? [{ "@type": "ListItem", "position": 2, "name": job.location, "item": `${SITE}/jobs/in/${_pCitySlug}` }] : []),
      { "@type": "ListItem", "position": _pCity ? 3 : 2, "name": job.title || fallbackTitle, "item": selfUrl },
    ],
  } : null;

  // Listing expired/removed — keep a real page (with an H1 + self canonical) rather
  // than redirecting, so the URL stays crawlable and users get a clear next step.
  if (!loading && notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-16" data-testid="external-job-notfound">
        <h1 className="font-display text-3xl md:text-4xl uppercase tracking-tighter text-white">{fallbackTitle || t.jobs.partnerBadge}</h1>
        <p className="mt-4 text-zinc-400">{t.jobs.partnerExpired || "This partner listing is no longer available."}</p>
        <Link to="/jobs" className="mt-6 btn-notch-primary inline-flex" data-testid="external-job-browse">{t.jobs.partnerBrowseAll || "Browse all jobs"}</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-10" data-testid="external-job-detail-page">
      <button onClick={() => navigate(-1)} className="btn-pill-ghost !text-xs" data-testid="external-job-back">← {t.jobs.partnerBack}</button>
      <div className="mt-4 card-elevated p-7">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-[#FF7A1A]/40 bg-[#FF7A1A]/10 text-[#FF7A1A] text-[10px] font-black uppercase tracking-[0.2em] rounded-sm" data-testid="external-job-badge">
          <Handshake size={12} weight="fill" /> {t.jobs.partnerBadge}
        </div>

        {/* Single H1 — always present (real title once loaded, humanized slug while loading) */}
        <div className="mt-4 flex items-start gap-4">
          {job?.company_logo && (
            <img
              src={_localLogo(job.company_logo)}
              alt={job.company ? `${job.company} logo` : "Company logo"}
              loading="lazy"
              className="w-14 h-14 rounded-xl shrink-0 object-cover border border-white/10"
              data-testid="external-job-logo"
            />
          )}
          <h1 className="font-display text-3xl md:text-4xl uppercase tracking-tighter text-white" data-testid="external-job-title">
            {job ? job.title : fallbackTitle}
          </h1>
        </div>

        {loading && !job ? (
          <div className="mt-6 space-y-3" data-testid="external-job-loading">
            <div className="h-4 w-2/3 bg-white/10 animate-pulse rounded" />
            <div className="h-4 w-1/2 bg-white/10 animate-pulse rounded" />
            <div className="h-24 w-full bg-white/5 animate-pulse rounded" />
          </div>
        ) : job ? (
          <>
            <div className="mt-3 flex items-center gap-3 flex-wrap text-zinc-400 text-sm">
              {job.company && <span className="flex items-center gap-1"><Briefcase size={13} weight="fill" /> {job.company}</span>}
              {job.location && <span className="flex items-center gap-1"><MapPin size={13} weight="fill" /> {job.location}</span>}
              {job.type && <span className="flex items-center gap-1"><Tag size={13} weight="fill" /> {job.type}</span>}
              {job.updated && <span className="flex items-center gap-1"><CalendarBlank size={13} weight="fill" /> {String(job.updated).slice(0, 10)}</span>}
            </div>

            {job.salary && <div className="mt-4 text-2xl font-display text-[#FF7A1A]" data-testid="external-job-salary">{job.salary}</div>}

            {(job.description || job.snippet) && (
              <>
                <h2 className="mt-6 font-display text-sm uppercase tracking-widest text-zinc-300">{t.jobs.partnerAbout}</h2>
                <p className="mt-2 text-zinc-200 whitespace-pre-wrap leading-relaxed" data-testid="external-job-description">{enr?.overview || job.description || job.snippet}</p>
              </>
            )}

            {enr?.responsibilities?.length > 0 && (
              <div data-testid="external-job-responsibilities">
                <h2 className="mt-7 font-display text-sm uppercase tracking-widest text-zinc-300 flex items-center gap-2"><ListChecks size={15} weight="fill" className="text-[#FF7A1A]" /> {t.jobs.partnerResponsibilities}</h2>
                <ul className="mt-3 space-y-2">
                  {enr.responsibilities.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-zinc-200 leading-relaxed">
                      <CheckCircle size={17} weight="fill" className="text-[#FF7A1A] mt-0.5 flex-shrink-0" /> <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {enr?.skills?.length > 0 && (
              <div data-testid="external-job-skills">
                <h2 className="mt-7 font-display text-sm uppercase tracking-widest text-zinc-300 flex items-center gap-2"><Wrench size={15} weight="fill" className="text-[#FF7A1A]" /> {t.jobs.partnerSkills}</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {enr.skills.map((s, i) => (
                    <span key={i} className="px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-zinc-200 text-sm">{s}</span>
                  ))}
                </div>
              </div>
            )}

            {enr?.pay_line && (
              <div data-testid="external-job-pay">
                <h2 className="mt-7 font-display text-sm uppercase tracking-widest text-zinc-300 flex items-center gap-2"><CurrencyDollar size={15} weight="fill" className="text-[#FF7A1A]" /> {t.jobs.partnerPay}</h2>
                <p className="mt-2 text-zinc-200 leading-relaxed">{enr.pay_line}</p>
              </div>
            )}

            {enr?.location_line && (
              <div data-testid="external-job-location-context">
                <h2 className="mt-7 font-display text-sm uppercase tracking-widest text-zinc-300 flex items-center gap-2"><MapPin size={15} weight="fill" className="text-[#FF7A1A]" /> {t.jobs.partnerLocationHeading}</h2>
                <p className="mt-2 text-zinc-200 leading-relaxed">{enr.location_line}</p>
              </div>
            )}

            {enr?.how_to_apply?.length > 0 && (
              <div data-testid="external-job-how-to-apply">
                <h2 className="mt-7 font-display text-sm uppercase tracking-widest text-zinc-300">{t.jobs.partnerHowToApply}</h2>
                <ol className="mt-3 space-y-2 list-decimal list-inside">
                  {enr.how_to_apply.map((s, i) => (
                    <li key={i} className="text-zinc-200 leading-relaxed pl-1">{s}</li>
                  ))}
                </ol>
              </div>
            )}

            {enr?.faqs?.length > 0 && (
              <div data-testid="external-job-faq">
                <h2 className="mt-7 font-display text-sm uppercase tracking-widest text-zinc-300 flex items-center gap-2"><Question size={15} weight="fill" className="text-[#FF7A1A]" /> {t.jobs.partnerFaq}</h2>
                <div className="mt-3 space-y-4">
                  {enr.faqs.map((f, i) => (
                    <div key={i}>
                      <h3 className="text-zinc-100 font-semibold">{f.q}</h3>
                      <p className="mt-1 text-zinc-300 leading-relaxed">{f.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="mt-6 text-xs text-zinc-500 flex items-start gap-1.5">
              <Warning size={14} weight="fill" className="text-[#FF7A1A] mt-0.5 flex-shrink-0" />
              {t.jobs.partnerNote}{job.source ? ` · ${t.jobs.partnerVia} ${job.source}` : ""}
            </p>

            {user ? (
              <a
                href={job.link}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="mt-7 btn-notch-primary w-full !justify-center"
                data-testid="external-job-apply-btn"
                onClick={() => trackPartnerEvent({ provider: job.provider, event: "outbound_click", job_id: job.external_id, job_category: job.type, job_location: job.location })}
              >
                {t.jobs.partnerApply} <ArrowSquareOut size={16} weight="bold" />
              </a>
            ) : (
              <button
                onClick={() => navigate(`/login?next=${encodeURIComponent(`/partner-jobs/${slug}`)}`)}
                className="mt-7 btn-notch-primary w-full !justify-center"
                data-testid="external-job-signin-to-apply-btn"
              >
                {t.jobs.partnerSignIn} <ArrowSquareOut size={16} weight="bold" />
              </button>
            )}

            <div className="mt-7 pt-6 border-t border-white/10">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-zinc-400" data-testid="external-share-heading">
                {t.jobs.partnerShareThis || "Share this job"}
              </p>
              <div className="mt-3">
                <ShareButtons
                  url={`${SITE}/api/share/partner-job/${slug}`}
                  message={`ChambaNOW is hiring for ${job.title}. Apply now — or share this with a friend!`}
                />
              </div>
            </div>
          </>
        ) : null}
      </div>

      {job?.related?.length > 0 && (
        <RelatedJobs related={job.related} heading={t.jobDetail?.relatedTitle || "Related jobs"} />
      )}

      {jsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} data-testid="jsonld-external-jobposting" />
      )}
      {faqLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} data-testid="jsonld-external-faq" />
      )}
      {breadcrumbLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} data-testid="jsonld-external-breadcrumb" />
      )}
    </div>
  );
}
